import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import type { Package, User } from "@/types";

interface DashboardStats {
  totalPackages: number;
  readyToShip: number;
  inTransit: number;
  totalSavings: number;
  pendingConfirmations: number;
}

function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalPackages: 0,
    readyToShip: 0,
    inTransit: 0,
    totalSavings: 0,
    pendingConfirmations: 0,
  });
  const [recentPackages, setRecentPackages] = useState<Package[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
  }, [user]);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);

      // Load packages
      const packagesResponse = await fetch(
        `${import.meta.env.DEV ? "http://localhost:8787" : "https://shopglobal-backend.finhub.workers.dev"}/api/packages`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
          },
        }
      );

      if (packagesResponse.ok) {
        const packagesData = await packagesResponse.json();
        const packages = packagesData.packages || [];

        setRecentPackages(packages.slice(0, 3));

        // Calculate stats
        const readyToShip = packages.filter(
          (p: Package) => p.status === "received" || p.status === "paid"
        ).length;
        const inTransit = packages.filter(
          (p: Package) => p.status === "shipped"
        ).length;
        const totalSavings = packages.reduce(
          (sum: number, pkg: Package) => sum + (pkg.savings || 0),
          0
        );
        const pendingConfirmations = packages.filter(
          (p: Package) => p.status === "incoming"
        ).length;

        setStats({
          totalPackages: packages.length,
          readyToShip,
          inTransit,
          totalSavings,
          pendingConfirmations,
        });
      }
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "received":
        return "text-green-600 bg-green-100";
      case "paid":
        return "text-blue-600 bg-blue-100";
      case "shipped":
        return "text-purple-600 bg-purple-100";
      case "delivered":
        return "text-gray-600 bg-gray-100";
      case "incoming":
        return "text-yellow-600 bg-yellow-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="font-bold text-sm">SG</span>
              </div>
              <span className="ml-2 font-bold text-lg">ShopGlobal Pro</span>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => (window.location.href = "/packages")}
                className="text-gray-600 hover:text-gray-900">
                All Packages
              </button>
              <button
                onClick={() => (window.location.href = "/calculator")}
                className="text-gray-600 hover:text-gray-900">
                Calculator
              </button>
              <div className="flex items-center text-gray-700">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white">
                  {user?.fullName?.[0]?.toUpperCase() || "U"}
                </div>
                <span className="ml-2">{user?.fullName}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Welcome back, {user?.fullName?.split(" ")[0] || "there"}!
          </h1>
          <p className="text-gray-600">Manage your packages and shipments</p>
        </div>

        {/* Delaware Address Card */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-6 text-white mb-8">
          <h2 className="text-xl font-bold mb-2">Your Delaware Address</h2>
          <div className="bg-white bg-opacity-20 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-mono text-lg">
                  {user?.delawareAddress || "Loading..."}
                </p>
                <p className="text-sm opacity-90">
                  Share this address with US retailers for package forwarding
                </p>
              </div>
              <button
                onClick={() =>
                  navigator.clipboard.writeText(user?.delawareAddress || "")
                }
                className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg">
                Copy
              </button>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Packages
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.totalPackages}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Ready to Ship
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.readyToShip}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">In Transit</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.inTransit}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Savings
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  ${stats.totalSavings}
                </p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-yellow-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"></path>
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.pendingConfirmations}
                </p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Packages */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-bold text-lg">Recent Packages</h3>
            <button
              onClick={() => (window.location.href = "/packages")}
              className="text-blue-600 hover:text-blue-700 text-sm">
              View All
            </button>
          </div>
          <div className="p-6">
            {recentPackages.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <svg
                  className="w-12 h-12 mx-auto mb-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
                </svg>
                <p>No packages yet</p>
                <p className="text-sm">
                  Send packages to your Delaware address to see them here
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {recentPackages.map((pkg) => (
                  <div
                    key={pkg.id}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center">
                      <div
                        className={`w-3 h-3 rounded-full mr-3 ${
                          pkg.status === "received"
                            ? "bg-green-500"
                            : pkg.status === "paid"
                              ? "bg-blue-500"
                              : pkg.status === "shipped"
                                ? "bg-purple-500"
                                : pkg.status === "delivered"
                                  ? "bg-gray-500"
                                  : "bg-yellow-500"
                        }`}></div>
                      <div>
                        <p className="font-medium">
                          {pkg.senderName || "Unknown Sender"}
                          {pkg.trackingNumber && ` - ${pkg.trackingNumber}`}
                        </p>
                        <p className="text-sm text-gray-600">
                          {formatDate(pkg.createdAt)} • {pkg.weight} lbs •{" "}
                          {pkg.declaredValue} CAD value
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs font-medium ${getStatusColor(pkg.status)}`}>
                        {pkg.status.charAt(0).toUpperCase() +
                          pkg.status.slice(1)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardPage;
